import React, { useCallback, useMemo } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { useRouter } from "next/router";
import ArrowBack from "@tiendeo/react-components/dist/atoms/Icons/ArrowBack";
import Header, { Logo, Variants } from "@tiendeo/react-components/dist/components/Header";
import ProfileButton from "@tiendeo/react-components/dist/objects/Buttons/ProfileButton";
import ProfileLoginButton from "@tiendeo/react-components/dist/objects/Buttons/ProfileLoginButton";
import Tiendeo from "@tiendeo/react-components/dist/atoms/Icons/Tiendeo";
import Spacer from "@tiendeo/react-components/dist/objects/Spacer/Spacer";

import useTranslation from "translations/useTranslation";
import { useServices } from "contexts/ServiceContext";
import useProfileQuery from "queries/useProfileQuery";
import ProfileMenuSidebarContainer from "./ProfileMenuSidebarContainer";
import Notifications from "ui/components/Notifications/Notifications";
import useCloseProfileMenu from "hooks/useCloseProfileMenu";
import useIsUnauthorized from "hooks/useIsUnauthorized";
import { EventEnum } from "enums/EventEnum";
import useWindowOpenWithAnalytics from "hooks/useWindowOpenWithAnalytics";
import useGetUnreadNotifications from "hooks/useGetUnreadNotifications";
import { configureScope } from "@sentry/node";

const LogoWrapper = styled.div`
    cursor: pointer;

    svg {
        fill: ${(props) => props.theme.colors.neutral.white};
    }
`;

interface IOwnProps {
    showTiendeoLogo: boolean;
    Title?: string;
}

type TProps = IOwnProps;

const HeaderContainer = ({ showTiendeoLogo, Title }: TProps) => {
    const services = useServices();
    const router = useRouter();

    const isProfileMenuOpen = useMemo(
        () => services.UrlsService.isProfileMenu(router.asPath),
        [router.asPath, services.UrlsService],
    );

    const onProfileMenuModalOpen = useCallback(() => {
        services.NavigationService.OpenProfileMenu(router.asPath, router.pathname, router.query, {
            shallow: true,
        });
    }, [router.asPath, router.pathname, router.query, services.NavigationService]);

    const closeProfileMenu = useCloseProfileMenu();

    const onProfileMenuClick = useCallback(() => {
        if (isProfileMenuOpen) {
            closeProfileMenu();
        } else {
            onProfileMenuModalOpen();
        }
    }, [isProfileMenuOpen, onProfileMenuModalOpen, closeProfileMenu]);

    const tryGoBack = useCallback(() => {
        if (showTiendeoLogo) {
            window.open(services.Configs.url_tiendeo, "_top", "noopener noreferrer");
        } else {
            services.NavigationService.MyTiendeo();
        }
    }, [showTiendeoLogo, services.NavigationService, services.Configs.url_tiendeo]);

    const profileQueryResult = useProfileQuery();
    const isUnauthorized = useIsUnauthorized(profileQueryResult);

    const numUnread = useGetUnreadNotifications();

    const { data: profile } = profileQueryResult;

    const t = useTranslation();
    const myTiendeo = t("MY_TIENDEO");

    return (
        <Header Variant={Variants.PRINCIPAL}>
            <div onClick={tryGoBack}>
                <Logo>
                    <LogoWrapper>
                        {showTiendeoLogo ? (
                            <Tiendeo className={"header__icon-tiendeo"} />
                        ) : (
                            <ArrowBack width={24} />
                        )}
                    </LogoWrapper>
                </Logo>
            </div>
            <span className="header__header-title disable-select" data-testid="backToLanding_title">
                {Title}
            </span>
            <Spacer />
            {!isUnauthorized && !!profile && services.Configs.enable_login ? (
                <ProfileButton
                    name={profile.name}
                    picture={profile.picture}
                    onClick={onProfileMenuClick}
                />
            ) : (
                <ProfileLoginButton text={myTiendeo} onClick={onProfileMenuClick} newIcon={false} />
            )}
            <CSSFixes />
            <ProfileMenuSidebarContainer />
        </Header>
    );
};

const CSSFixes = createGlobalStyle`
    #header-account-button {
        & > p {
            font-size: 1.3em;
            letter-spacing: .03rem;
        }

        & > svg {
            fill: ${(props) => props.theme.colors.neutral.white};
        }
    }

    /* It is included in the previous ltr-style.css file but not here */
    .spacer {
        flex: 1 1;
    }

    /* It is included in the previous ltr-style.css file but not here */
    .--small {
        height: 24px;
    }
`;

export default HeaderContainer;
